import { useState, useEffect } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { Button } from '../ui/Button'

export interface ConfirmPostApprovalEditDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: string) => void | Promise<void>
  title?: string
  documentCode?: string
  loading?: boolean
  description?: string
  reasonRequired?: boolean
  minimumReasonLength?: number
  reasonLabel?: string
  confirmLabel?: string
}

export function ConfirmPostApprovalEditDialog({
  isOpen,
  onClose,
  onConfirm,
  title = 'Xác nhận chỉnh sửa sau duyệt',
  documentCode,
  loading = false,
  description,
  reasonRequired = false,
  minimumReasonLength = 0,
  reasonLabel,
  confirmLabel = 'Xác nhận sửa',
}: ConfirmPostApprovalEditDialogProps) {
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (isOpen) {
      setReason('')
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <span>{title}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
            aria-label="Đóng"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div className="rounded-[var(--radius-md)] border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 leading-relaxed">
            <p className="font-semibold mb-1">Lưu ý quan trọng:</p>
            <p>
              {description ?? `${documentCode ? `PO ${documentCode}` : 'PO'} đã được duyệt. Sửa sẽ có hiệu lực ngay, không cần duyệt lại và được ghi vào lịch sử thay đổi.`}
            </p>
          </div>

          <div>
            <label
              htmlFor="post-approval-edit-reason"
              className="block text-xs font-medium text-slate-700 mb-1"
            >
              {reasonLabel ?? 'Lý do chỉnh sửa (tùy chọn)'}
            </label>
            <textarea
              id="post-approval-edit-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Nhập lý do chỉnh sửa để ghi vào lịch sử..."
              disabled={loading}
              className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] p-2 text-xs focus:border-[var(--color-accent)] focus:outline-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={loading}
            >
              Hủy bỏ
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={loading || (reasonRequired && reason.trim().length < minimumReasonLength)}
              onClick={() => onConfirm(reason.trim())}
            >
              {loading ? 'Đang lưu...' : confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
